import React, { useState, useEffect, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    get as _get,
    cloneDeep as _cloneDeep
} from 'lodash';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import { DataGridPro, useGridApiRef as useApiRef } from '@mui/x-data-grid-pro';

import CheckboxOutlineIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckboxIcon from '@mui/icons-material/CheckBoxOutlined';

import Auth, { ProfileContext } from '../../data/auth-service';
import CMCsService from '../../data/cmcs-service';
import SensorService from '../../data/sensors-service';
import PolicyService from '../../data/policy-service';
import BacktestingService from '../../data/backtesting-service';
import { neutrals2 } from '../../data/colors';

import useWindowSize from '../../libraries/usewindowsize';
import Helpers from '../../libraries/helpers';
import { LoadingOverlay } from '../xgrid/overlays';

const useStyles = makeStyles(theme => ({
    justifySpace: {
        justifyContent: 'space-between',
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(1),
    },
    pushDrawerContainer: {
        background: theme.palette.background.default,
    },
    pushDrawer: {
        width: window.innerWidth * 0.50,
        padding: theme.spacing(3),
    },
    infoBox: {
        border: `1px solid ${theme.palette.primary.main}`,
        background: theme.styled.paper.background,
        padding: theme.spacing(2),
    },
    pushPolicy: {
        display: 'inline',
        margin: '0 3px',
        padding: '5px 10px',
        background: theme.palette.primary.main,
        borderRadius: '5px',
        color: theme.palette.primary.contrastText,
    },
    filterFields: {
        marginRight: theme.spacing(1),
        marginLeft: theme.spacing(1),
        minHeight: '32px',
    },
    filterContainer: {
        border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
        borderRadius: '5px',
        borderBottom: 'none',
        marginBottom: '-1px',
    },
}));

const Push = (props) => {
    const classes = useStyles();
    const MC2CHECK = (process.env.BRICATA_APPLIANCE_TYPE === 'mc2');
    const [original, setOriginal] = useState([]);
    const [filteredSensors, setFilteredSensors] = useState([]);
    const [deployRequested, setDeployRequested] = useState(false);
    const [cmcs, setCMCs] = useState([]);
    const [originalSelection, setOriginalSelection] = useState(MC2CHECK ? {} : []);
    const [selectedSensors, setSelectedSensors] = useState(MC2CHECK ? {} : []);
    const [, setInit] = useState(true);
    const [filter, setFilter] = useState({
        q: '',
        name: '',
        cmc: '',
        host: '',
        model: '',
        status: {
            critical: true,
            warning: true,
            normal: true,
        },
        policies: [/* {name: '', type: ''} */],
        sort: 'status+desc'
    });
    const { height } = useWindowSize();
    const tableAPIRef = useApiRef();
    const { updateProfile, getProfile } = useContext(ProfileContext);
    const [columnWidth, setColumnWidth] = useState({});
    const [policies, setPolicies] = useState([]);
    const sensorPerms = Auth.permissions.get('Sensors');
    const cmcPerms = Auth.permissions.get('CMC');
    const policyPerms = Auth.permissions.get(props.permName);

    useEffect(() => {
        if (MC2CHECK) {
            getCMCs();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // not ready to fetch sensors yet
        if (MC2CHECK && cmcs.length < 1) return;

        MC2CHECK && getSensors(buildFilter());
        setInit(false);
        getPolicies();

        setColumnWidth(getProfile('push').widths || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [MC2CHECK, cmcs]);

    useEffect(() => {
        MC2CHECK ? runFilter(original) : getSensors(buildFilter());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    useEffect(() => {
        if (!deployRequested || props.pushing) return;

        getSensors(buildFilter(), true);
        setDeployRequested(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deployRequested, props.pushing]);

    useEffect(() => {
        if (MC2CHECK && cmcs.length < 1) return;

        const interval = setInterval(() => {
            getSensors(buildFilter(), true);
        }, 30000);

        return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [MC2CHECK, cmcs, filter]);

    const runFilter = (c) => {
        const s = _cloneDeep(c)
            .filter(v => {
                if (
                    (_get(v, 'name', '').includes(filter.name)) ||
                    (_get(v, 'data.name', '').includes(filter.name))
                ) {
                    return v;
                }
                return null;
            })
            .filter(v => {
                if (_get(v, 'host', '').includes(filter.host)) return v;
                return null;
            })
            .filter(v => {
                if (_get(v, 'model', '').includes(filter.model)) return v;
                return null;
            })
            .filter(v => {
                if (_get(v, 'cmc', '').includes(filter.cmc)) return v;
                return null;
            });

        setFilteredSensors(s);
    };

    const handleColumnResize = params => {
        const widths = tableAPIRef.current.getAllColumns().reduce((a, c) => {
            a[c.headerName] = c.width;
            return a;
        }, {});
        updateProfile('push', {
            ...getProfile('push'),
            widths: widths,
        });
        setColumnWidth(widths);
    };

    const handleColumnReorder = () => {
        let order = [];
        setTimeout(() => {
            order = tableAPIRef.current.getAllColumns().reduce((a, c) => {
                a.push(c.headerName);
                return a;
            }, []);

            updateProfile('push', {
                ...getProfile('push'),
                columns: order,
            });
        });
    };

    const getPolicies = () => {
        PolicyService.getPolicies(props.policyType).then(res => {
            setPolicies(res);
        }, err => {
            console.error(err);
        });
    };

    const getSensors = (search, preserveSelection = false) => {
        if (sensorPerms.read) {
            SensorService.getData(search).then(res => {
                if (!res) {
                    res = [];
                }
                if (BacktestingService.policies[props.policyType]) {
                    res = res.filter(v => !v.data.backtesting);
                }
                res = res.map(v => { return { ...v, id: Helpers.UniqueID() } });
                // setSensors(res);
                if (MC2CHECK) {
                    const ss = res
                        .filter(f => {
                            // Check database for purported policy only
                            if (Object.prototype.hasOwnProperty.call(_get(f, 'data.jobs', {}), props.policyType)) {
                                return getDBPolicy(f).name === props.policyName;
                            }
                            return null;
                        })
                        .reduce((a, c) => {
                            const cmc = getCMCUUID(c.cmc);
                            if (!a[cmc]) a[cmc] = {};
                            a[cmc] = { sensors: [...(a[cmc] || {}).sensors || [], c.uuid] };
                            return a;
                        }, {});
                    if (preserveSelection) {
                        setSelectedSensors(current => {
                            const availableUUIDs = new Set(res.map(v => v.uuid));
                            const merged = _cloneDeep(current || {});

                            Object.keys(merged).forEach(k => {
                                const arr = ((merged[k] || {}).sensors || []).filter(uuid => availableUUIDs.has(uuid));
                                merged[k] = { sensors: arr };
                            });

                            return merged;
                        });
                    } else {
                        setSelectedSensors(ss);
                        setOriginalSelection(_cloneDeep(ss));
                    }
                } else {
                    const initialSelection = res.filter(f => {
                        if (Object.prototype.hasOwnProperty.call(f.deliveries || {}, props.policyType)) {
                            return f.deliveries[props.policyType].name === props.policyName;
                        }
                        return null;
                    }).map(v => v.uuid);

                    if (preserveSelection) {
                        setSelectedSensors(current => {
                            const availableUUIDs = new Set(res.map(v => v.uuid));
                            return (current || []).filter(uuid => availableUUIDs.has(uuid));
                        });
                    } else {
                        setSelectedSensors(initialSelection);
                    }
                }
                setFilteredSensors(res);
                setOriginal(res);
                setInit(false);
            });
        }
    };

    const getCMCs = () => {
        if (cmcPerms.read) {
            CMCsService.list().then(res => {
                setCMCs(res);
            });
        }
    };

    const buildFilter = () => {
        const f = Object.keys(filter).reduce((a, c) => {
            switch (c) {
                case 'status':
                    a = Array.prototype.concat(a, Object.keys(filter[c]).map((v) => {
                        if (filter[c][v]) return 'status=' + encodeURIComponent(v);
                        return null;
                    }));
                    break;
                case 'sort':
                    a.push('sort=' + filter[c]);
                    break;
                case 'policies':
                    a = Array.prototype.concat(a, filter[c].map((v) => {
                        return 'filter=' + encodeURIComponent(`${v.type}=${v.name}`);
                    }));
                    break;
                default:
                    if (filter[c]) a.push('filter=' + encodeURIComponent(`${c}=${filter[c]}`));
            }

            return a;
        }, []);

        return f.join('&');
    };

    const handleUpdateFilter = (e) => {
        setFilter({
            ...filter,
            [e.target.name]: e.target.value,
        });
    };

    const handleDeploy = () => {
        setDeployRequested(true);
        props.onDeploy(selectedSensors);
    };

    const getCMCUUID = (name) => {
        return cmcs.reduce((a, c) => {
            return c.name === name ? c.uuid : a;
        }, '');
    };

    const handleAddSensor = (uuid, cmc) => {
        if (MC2CHECK) {
            const s = { ...selectedSensors };
            const cmcUUID = getCMCUUID(cmc);

            if (!s[cmcUUID]) {
                s[cmcUUID] = { sensors: [] };
            }
            if (s[cmcUUID].sensors.includes(uuid)) {
                s[cmcUUID].sensors.splice(s[cmcUUID].sensors.indexOf(uuid), 1);
            } else {
                s[cmcUUID].sensors.push(uuid);
            }
            setSelectedSensors(s);
        } else {
            const s = [...selectedSensors];
            if (s.includes(uuid)) {
                s.splice(s.indexOf(uuid), 1);
            } else {
                s.push(uuid);
            }
            setSelectedSensors(s);
        }
    };

    const handleSelectAllInFilter = () => {
        if (MC2CHECK) {
            const o = originalSelection;
            const s = { ...selectedSensors };
            filteredSensors.forEach(v => {
                const cmcUUID = getCMCUUID(v.cmc);
                const ignore = Object.keys(o).reduce((a, c) => a.concat(o[c].sensors), []).reduce((a, c) => a || c === v.uuid, false);
                if (!ignore) {
                    if (!s[cmcUUID]) {
                        s[cmcUUID] = { sensors: [] };
                    }
                    if (!s[cmcUUID].sensors.includes(v.uuid)) {
                        s[cmcUUID].sensors.push(v.uuid);
                    }
                }
            });
            setSelectedSensors(s);
        } else {
            const s = [...selectedSensors];
            filteredSensors
                .filter(v => !isMC2Managed(v) || policyPerms.override)
                .forEach(v => {
                    if (!original.includes(v.uuid) && !s.includes(v.uuid)) {
                        s.push(v.uuid);
                    }
                });
            setSelectedSensors(s);
        }
    };

    const handleDeselectAllInFilter = () => {
        if (MC2CHECK) {
            const o = originalSelection;
            const s = { ...selectedSensors };
            filteredSensors.forEach(v => {
                const cmcUUID = getCMCUUID(v.cmc);
                const ignore = Object.keys(o).reduce((a, c) => a.concat(o[c].sensors), []).reduce((a, c) => a || c === v.uuid, false);
                if (!ignore) {
                    if (
                        !s[cmcUUID] ||
                        !s[cmcUUID].sensors ||
                        !s[cmcUUID].sensors.length ||
                        !s[cmcUUID].sensors.includes(v.uuid)
                    ) return;
                    if (s[cmcUUID].sensors.includes(v.uuid)) {
                        s[cmcUUID].sensors.splice(s[cmcUUID].sensors.indexOf(v.uuid), 1);
                    }
                }
            });
            setSelectedSensors(s);
        } else {
            const s = [...selectedSensors];
            filteredSensors
                .filter(v => !isMC2Managed(v) || policyPerms.override)
                .forEach((v) => {
                    if (s.includes(v.uuid)) {
                        s.splice(s.indexOf(v.uuid), 1);
                    }
                });
            setSelectedSensors(s);
        }
    };

    const getDBPolicy = useCallback(row => {
        const dapolicy = _get(row, `data.jobs.${props.policyType}`, { name: 'default' });
        const p = policies.find(v => v.name === dapolicy.name);
        return {
            ...dapolicy,
            color: (p || {}).color,
        };
    }, [policies, props.policyType]);

    const isMC2Managed = useCallback(row => {
        const dapolicy = _get(row, `data.jobs.${props.policyType}`, { name: 'default' });
        if (dapolicy.name === 'default') return false;

        const p = policies.find(v => v.name === dapolicy.name);
        return _get(p, 'source') === 'mc2';
    }, [policies, props.policyType]);

    const getSensorPolicy = useCallback(row => {
        const dapolicy = _get(row, `deliveries.${props.policyType}`, { name: 'default' });
        const p = policies.find(v => v.name === dapolicy.name);
        return {
            name: dapolicy.name,
            color: (p || {}).color,
        };
    }, [policies, props.policyType]);

    const tableRenderers = {
        name: d => { return <div>{_get(d, 'row.data.name', '')}</div> },
        model: d => { return <div>{_get(d, 'row.model', '')}</div> },
        host: d => { return <div>{_get(d, 'row.host', '')}</div> },
        cmc: d => { return <div>{_get(d, 'row.cmc', '')}</div> },
        policies: d => {
            const p = [];

            // row.deliveries reflects what jobs the sensor last reported in health json
            // row.data.jobs reflects what jobs the database thinks this sensor has
            // when sensors are offline or there are other issues delivering jobs
            // you'll see discrepancies between the two
            const dbWants = getDBPolicy(d.row);
            const sensorWants = getSensorPolicy(d.row);
            p.push(
                <span
                    key={'delivery-' + dbWants.name}
                    className={classes.pushPolicy}
                    style={{ background: dbWants.color || '#24A5E9' }}
                >{dbWants.name}</span>
            );
            if (dbWants.name !== sensorWants.name) {
                p.push(
                    <span
                        key={'delivery-' + sensorWants.name}
                        className={classes.pushPolicy}
                        style={{ background: neutrals2[200], color: 'black' }}
                    >{sensorWants.name} <small>(sensor reported)</small>
                    </span>
                );
            }
            return <div>{p}</div>;
        },
        enabled: d => {
            if (isMC2Managed(d.row) && !policyPerms.override) {
                return <span> Managed by MC2 </span>;
            }
            const o = originalSelection;
            return (
                <FormControlLabel
                    label=""
                    control={
                        <Checkbox
                            disabled={Object.keys(o).reduce((a, c) => a.concat(o[c].sensors), []).reduce((a, c) => (a || c === d.row.uuid), false)}
                            checked={(() => {
                                if (Object.prototype.toString.call(selectedSensors).includes('Array')) {
                                    return selectedSensors.includes(d.row.uuid) || original.includes(d.row.uuid);
                                }
                                return Boolean(Object.keys(selectedSensors).map(v => {
                                    return selectedSensors[v].sensors.includes(d.row.uuid);
                                }).filter(v => v).length) || original.includes(d.row.uuid);
                            })()}
                            onChange={() => { handleAddSensor(d.row.uuid, d.row.cmc) }}
                        />
                    }
                />
            );
        }
    };

    const getColumns = useCallback(() => {
        const tableOpts = {
            sortable: true,
            type: 'string',
            disableColumnMenu: true,
            hideable: false,
            hide: false,
        };
        const p = getProfile('push');

        let c = [
            { ...tableOpts, width: columnWidth.Name || 0, ...(!columnWidth.Name ? { flex: 1 } : {}), headerName: 'Name', field: 'data.name', renderCell: tableRenderers.name },
            { ...tableOpts, width: columnWidth.Host || 0, ...(!columnWidth.Host ? { flex: 1 } : {}), headerName: 'Host', field: 'host', renderCell: tableRenderers.host },
            { ...tableOpts, width: columnWidth.Model || 0, ...(!columnWidth.Model ? { flex: 1 } : {}), headerName: 'Model', field: 'model', renderCell: tableRenderers.model },
            { ...tableOpts, width: columnWidth['Current policy'] || 0, ...(!columnWidth['Current policy'] ? { flex: 1 } : {}), headerName: 'Current policy', field: 'deliveries', renderCell: tableRenderers.policies },
            { ...tableOpts, width: columnWidth.Enabled || 0, ...(!columnWidth.Enabled ? { flex: 0.5 } : {}), headerName: 'Enabled', field: 'enabled', renderCell: tableRenderers.enabled },
        ];

        if (MC2CHECK) {
            c.splice(1, 0, {
                ...tableOpts,
                width: columnWidth.CMC || 0,
                ...(!columnWidth.CMC ? { flex: 0.5 } : {}),
                headerName: 'CMC',
                field: 'cmc',
                renderCell: tableRenderers.cmc,
            });
        }

        if (p.columns && p.columns.length) {
            c = p.columns.map(v => {
                for (let i = c.length; i--;) {
                    if (v === c[i].headerName) {
                        return c[i];
                    }
                }
                return null;
            });
        }

        return c;
    }, [MC2CHECK, columnWidth, getProfile, tableRenderers.cmc, tableRenderers.enabled, tableRenderers.host, tableRenderers.model, tableRenderers.name, tableRenderers.policies]);

    return (
        <React.Fragment>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Box mr={2} display="inline-block"><Typography variant="h4">Push Confirmation</Typography></Box>
                    <Box my={1} className={classes.infoBox} display="inline-block">
                        Select additional sensors you&apos;d like to deploy the policy to.
                        If you&apos;re updating the policy click the Confirm
                        button to continue using the same sensors as before.
                    </Box>
                    <Box py={1} px={2} className={classes.filterContainer} display="flex" justifyContent="space-between">
                        <Box display="flex" flexGrow="1">
                            <Box>
                                <Typography variant="h6" style={{ display: 'inline-block', margin: '10px' }}>Filter</Typography>
                            </Box>
                            <Box flexGrow={1}>
                                {/* <TextField className={classes.filterFields} fullWidth type="text" label="Search" name="q" value={filter.q} onChange={handleUpdateFilter} /> */}
                                <TextField variant="standard" className={classes.filterFields} type="text" label="Name" name="name" value={filter.name} onChange={handleUpdateFilter} />
                                {MC2CHECK && <TextField variant="standard" className={classes.filterFields} type="text" label="CMC" name="cmc" value={filter.cmc} onChange={handleUpdateFilter} />}
                                <TextField variant="standard" className={classes.filterFields} type="text" label="Host" name="host" value={filter.host} onChange={handleUpdateFilter} />
                                <TextField variant="standard" className={classes.filterFields} type="text" label="Model" name="model" value={filter.model} onChange={handleUpdateFilter} />
                            </Box>
                        </Box>
                        <Box display="flex" justifyContent="flex-end">
                            <Tooltip title="Select All"><IconButton onClick={handleSelectAllInFilter} size="large"><CheckboxIcon /></IconButton></Tooltip>
                            <Tooltip title="Deselect All"><IconButton onClick={handleDeselectAllInFilter} size="large"><CheckboxOutlineIcon /></IconButton></Tooltip>
                        </Box>
                    </Box>
                    <Box height={(height < 740 ? 265 : height - 500)}>
                        <DataGridPro
                            components={{ LoadingOverlay }}
                            apiRef={tableAPIRef}
                            loading={props.pushing}
                            rows={filteredSensors}
                            columns={getColumns()}
                            disableMultipleColumnsFiltering
                            disableMultipleColumnsSorting
                            disableMultipleSelection
                            disableSelectionOnClick
                            disableColumnFilter
                            disableColumnMenu
                            onColumnResize={handleColumnResize}
                            onColumnOrderChange={handleColumnReorder}
                            disableVirtualization
                        />
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Box pt={2} mb={10} display="flex" justifyContent="flex-end">
                        <Box ml={2}><Button disabled={props.pushing} variant="contained" color="secondary" onClick={props.closePush}>Cancel</Button></Box>
                        <Box ml={2}><Button disabled={props.pushing} variant="contained" color="primary" onClick={handleDeploy}>Confirm</Button></Box>
                    </Box>
                </Grid>
            </Grid>
        </React.Fragment>
    );
};

Push.propTypes = {
    onDeploy: PropTypes.func.isRequired,
    policyType: PropTypes.string.isRequired,
    policyName: PropTypes.string.isRequired,
    closePush: PropTypes.func.isRequired,
    pushing: PropTypes.bool,
    permName: PropTypes.string.isRequired,
};

export default Push;
